import React from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { DesignerDocument, DesignerNode } from './model';

export interface SearchOptions { query: string; replacement: string; caseSensitive: boolean; wholeWord: boolean }
export interface SearchHit { nodeId: string; itemId?: string; cellKey?: string; from: number; to: number }

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function searchPattern(options: SearchOptions): RegExp | null {
  if (!options.query) return null;
  const body = escapeRegExp(options.query);
  const source = options.wholeWord ? `(?<![\\p{L}\\p{N}_])${body}(?![\\p{L}\\p{N}_])` : body;
  try {
    return new RegExp(source, options.caseSensitive ? 'gu' : 'giu');
  } catch {
    return new RegExp(body, options.caseSensitive ? 'g' : 'gi');
  }
}

/** Alle Treffer in Absätzen, Listen und Tabellenzellen – in Dokumentreihenfolge. */
export function collectHits(document: DesignerDocument, options: SearchOptions): SearchHit[] {
  const pattern = searchPattern(options);
  if (!pattern) return [];
  const hits: SearchHit[] = [];
  const scan = (text: string, base: Omit<SearchHit, 'from' | 'to'>) => {
    pattern.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      hits.push({ ...base, from: match.index, to: match.index + match[0].length });
      if (match.index === pattern.lastIndex) pattern.lastIndex++;
    }
  };
  const walk = (nodes: DesignerNode[]) => nodes.forEach(node => {
    if (node.type === 'paragraph' || node.type === 'heading') scan(node.text || '', { nodeId: node.id });
    else if (node.type === 'list') (node.items || []).forEach(item => scan(item.text || '', { nodeId: node.id, itemId: item.id }));
    else if (node.type === 'container') (node.columns || []).forEach((column: any) => walk(column.nodes || []));
    else if (node.type === 'table') (node.rows || []).forEach((row: any) => (node.columns || []).forEach((column: any) => scan(String(row.cells?.[column.key] ?? ''), { nodeId: node.id, itemId: row.id, cellKey: column.key })));
  });
  walk(document.nodes);
  return hits;
}

export default function FindReplace({ options, setOptions, hits, index, onNavigate, onReplace, onReplaceAll, onClose }: {
  options: SearchOptions;
  setOptions: (next: SearchOptions) => void;
  hits: SearchHit[];
  index: number;
  onNavigate: (delta: number) => void;
  onReplace: () => void;
  onReplaceAll: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-lg p-2 shadow-sm" role="search" aria-label="Suchen und Ersetzen">
      <input
        autoFocus
        aria-label="Suchbegriff"
        placeholder="Suchen …"
        className="border border-gray-300 rounded p-1.5 text-sm w-48"
        value={options.query}
        onChange={event => setOptions({ ...options, query: event.target.value })}
        onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); onNavigate(event.shiftKey ? -1 : 1); } if (event.key === 'Escape') onClose(); }}
      />
      <input
        aria-label="Ersetzen durch"
        placeholder="Ersetzen durch …"
        className="border border-gray-300 rounded p-1.5 text-sm w-48"
        value={options.replacement}
        onChange={event => setOptions({ ...options, replacement: event.target.value })}
      />
      <span className="text-xs text-gray-600 tabular-nums" role="status">{hits.length ? `${index + 1} von ${hits.length}` : 'keine Treffer'}</span>
      <button type="button" aria-label="Vorheriger Treffer" className="p-1.5 rounded hover:bg-gray-100" onClick={() => onNavigate(-1)}><ChevronUp size={15} /></button>
      <button type="button" aria-label="Nächster Treffer" className="p-1.5 rounded hover:bg-gray-100" onClick={() => onNavigate(1)}><ChevronDown size={15} /></button>
      <button type="button" className="text-xs border rounded px-2 py-1.5 hover:bg-gray-50" onClick={onReplace} disabled={!hits.length}>Ersetzen</button>
      <button type="button" className="text-xs border rounded px-2 py-1.5 hover:bg-gray-50" onClick={onReplaceAll} disabled={!hits.length}>Alle ersetzen</button>
      <label className="flex items-center gap-1 text-xs text-gray-600"><input type="checkbox" checked={options.caseSensitive} onChange={event => setOptions({ ...options, caseSensitive: event.target.checked })} />Groß-/Kleinschreibung</label>
      <label className="flex items-center gap-1 text-xs text-gray-600"><input type="checkbox" checked={options.wholeWord} onChange={event => setOptions({ ...options, wholeWord: event.target.checked })} />Nur ganze Wörter</label>
      <button type="button" aria-label="Suche schließen" className="ml-auto p-1.5 rounded hover:bg-gray-100" onClick={onClose}><X size={15} /></button>
    </div>
  );
}
